import { NavLink, Navigate, Outlet } from "react-router-dom";
import { useMemo } from "react";
import {
  LayoutDashboard, Building2, FileText, Newspaper, Image as ImageIcon,
  FolderOpen, MessageSquareQuote, CalendarClock, Mail, Search,
  BarChart3, Users, ShieldCheck, ClipboardCheck, History, Settings, LogOut,
} from "lucide-react";
import gggLogo from "@/assets/logos/ggg-logo-final.webp";
import { useAdminAuth } from "@/admin/AdminAuthContext";

const NAV_ITEMS = [
  { to: "/admin/dashboard", label: "Dashboard", icon: LayoutDashboard, module: "dashboard" },
  { to: "/admin/brands", label: "Brands", icon: Building2, module: "brands" },
  { to: "/admin/pages", label: "Pages", icon: FileText, module: "pages" },
  { to: "/admin/blog", label: "Blog", icon: Newspaper, module: "blog" },
  { to: "/admin/gallery", label: "Gallery", icon: ImageIcon, module: "gallery" },
  { to: "/admin/media", label: "Media Library", icon: FolderOpen, module: "media" },
  { to: "/admin/testimonials", label: "Testimonials", icon: MessageSquareQuote, module: "testimonials" },
  { to: "/admin/appointments", label: "Appointments", icon: CalendarClock, module: "appointments" },
  { to: "/admin/newsletter", label: "Newsletter", icon: Mail, module: "newsletter" },
  { to: "/admin/seo", label: "SEO", icon: Search, module: "seo" },
  { to: "/admin/analytics", label: "Analytics", icon: BarChart3, module: "analytics" },
  { to: "/admin/users", label: "Users", icon: Users, module: "users" },
  { to: "/admin/roles", label: "Roles", icon: ShieldCheck, module: "roles" },
  { to: "/admin/approvals", label: "Approvals", icon: ClipboardCheck, module: "approvals" },
  { to: "/admin/audit-log", label: "Audit Log", icon: History, module: "audit_log" },
  { to: "/admin/settings", label: "Settings", icon: Settings, module: "settings" },
];

const AdminLayout = () => {
  const { user, loading, logout, can } = useAdminAuth();

  // NAV_ITEMS is a fixed module-level array; recomputing this filter on
  // every render is unnecessary work (it can only actually change when
  // the current admin's permission set -- `can` -- changes).
  const visibleNavItems = useMemo(
    () => NAV_ITEMS.filter((item) => can(item.module, "read")),
    [can]
  );

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center" style={{ background: "var(--gg-bg-black)" }}>Loading…</div>;
  }
  if (!user) {
    return <Navigate to="/admin/login" replace />;
  }

  return (
    <div className="min-h-screen flex" style={{ background: "var(--gg-bg-black)" }} data-testid="admin-layout">
      <aside className="w-[260px] shrink-0 hidden md:flex flex-col border-r" style={{ borderColor: "var(--gg-fine-border)" }}>
        <div className="p-6 flex items-center gap-3 border-b" style={{ borderColor: "var(--gg-fine-border)" }}>
          <img src={gggLogo} alt="Gopi Gadhvi Group" className="h-9 w-auto object-contain" />
          <span className="eyebrow" style={{ fontSize: "0.65rem" }}>Command Centre</span>
        </div>
        <nav className="flex-1 overflow-y-auto py-4 px-3 space-y-1">
          {visibleNavItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              data-testid={`admin-nav-${item.module}`}
              className={({ isActive }) =>
                `flex items-center gap-3 px-4 py-2.5 rounded-xl text-small font-semibold transition-colors duration-200 ${isActive ? "" : "opacity-70 hover:opacity-100"}`
              }
              style={({ isActive }) => ({
                background: isActive ? "var(--gg-raised-surface)" : "transparent",
                color: isActive ? "var(--gg-logo-gold)" : "var(--gg-ivory)",
              })}
            >
              <item.icon size={16} /> {item.label}
            </NavLink>
          ))}
        </nav>
        <div className="p-4 border-t" style={{ borderColor: "var(--gg-fine-border)" }}>
          <p className="text-small font-semibold" style={{ color: "var(--gg-ivory)" }}>{user.full_name}</p>
          <p className="text-small mb-3">{user.role_name}{user.is_owner ? " · Owner" : ""}</p>
          <button onClick={logout} className="btn-outline-silver btn-pill-sm w-full inline-flex justify-center" data-testid="admin-logout-btn">
            <LogOut size={14} /> Sign Out
          </button>
        </div>
      </aside>

      <main className="flex-1 overflow-y-auto p-6 md:p-10">
        <Outlet />
      </main>
    </div>
  );
};

export default AdminLayout;
